import { FC, PropsWithChildren, useEffect, useState } from 'react'
import css from 'styled-jsx/css'
import { getCardType, iconPath } from '@ui/helpers'
import { CustomerAddress } from '@libs/client/order/services'
import { localpaymentTypeOrderKey, useGlobalState } from '@libs/client'

interface CustomerAddressOrdTracking extends CustomerAddress {
  province?: string
  province_code?: string
  zip?: string
}
interface ICustomerInfo {
  paymentMethod: 'PAYFLOW' | 'STRIPE' | 'PAYPAL' | 'BRAINTREE'
  billingAddress?: CustomerAddressOrdTracking
  shippingAddress?: CustomerAddressOrdTracking
  merchant_name?: string
  isLoading: boolean
  cardInfo?: SfTypes.CardInfo
  AddressOption?: JSX.Element | null
  AddressForm?: JSX.Element | null
  customerEmail: string
}

const CustomerInfoOrdTracking: FC<PropsWithChildren<ICustomerInfo>> = ({
  paymentMethod,
  shippingAddress,
  merchant_name,
  cardInfo,
  customerEmail
}: PropsWithChildren<ICustomerInfo>) => {
  const email = customerEmail ?? ''

  const [cardType, setCardType] = useState<string>(iconPath('paypal_icon.svg'))
  const [paymentTypeOrder] = useGlobalState<string | null>(localpaymentTypeOrderKey)

  function onGetCardInfo(info?: SfTypes.CardInfo) {
    if (!info || paymentMethod === 'PAYPAL') {
      return
    }
    setCardType(getCardType(info.card_first4 ?? '', paymentTypeOrder ?? info.card_brand ?? ''))
  }

  useEffect(() => {
    onGetCardInfo(cardInfo)
  }, [cardInfo])

  return (
    <>
      <style jsx>{ScopeStyle}</style>
      <div className="customer-info">
        <div className="columns is-multiline">
          <div className="column is-half">
            <div className="customer-info__label">Contact Information</div>
            <div>{email}</div>
          </div>
          <div className="column is-half">
            <div className="customer-info__label has-icon">Payment Method</div>
            {paymentMethod === 'STRIPE' || paymentMethod === 'BRAINTREE' ? (
              <>
                <div className="customer-info__payment">
                  <figure className="image customer-info__payment--card">
                    <img src={cardType} alt="Payment Type" />
                  </figure>
                  <span className="customer-info__payment--number">
                    ****{cardInfo?.card_last4 ?? ''}
                  </span>
                </div>
              </>
            ) : (
              <div>
                <figure className="image customer-info__payment--card">
                  <img src={cardType} alt="Payment Type" />
                </figure>
                {merchant_name && (
                  <p className="payment-merchant">
                    On bank statement, <br />
                    {merchant_name} will appear as the vendor
                  </p>
                )}
              </div>
            )}
          </div>
          {shippingAddress ? (
            <div className="column is-half">
              <div className="customer-info__label">Shipping address</div>
              <div>
                {(shippingAddress.first_name ?? '') + ' ' + (shippingAddress.last_name ?? '')}
              </div>
              <div>{shippingAddress.address1 ?? ''}</div>
              <div>{shippingAddress.address2 ?? ''}</div>
              {(shippingAddress.city ||
                shippingAddress.state ||
                shippingAddress.postal_code ||
                shippingAddress.province ||
                shippingAddress.zip) && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {`${shippingAddress.city ?? ''} ${
                    shippingAddress.state ?? shippingAddress.province ?? ''
                  } ${shippingAddress.postal_code ?? shippingAddress.zip ?? ''}`}
                </div>
              )}
              <div>{shippingAddress.country ?? ''}</div>
              <div>{shippingAddress.phone ?? ''}</div>
            </div>
          ) : null}
          {/* {billingAddress ? (
            <div className="column is-half">
              <div className="customer-info__label">Billing address</div>
              <div>
                {(billingAddress.first_name ?? '') + ' ' + (billingAddress.last_name ?? '')}
              </div>
              <div>{billingAddress.address1 ?? ''}</div>
              <div>{billingAddress.address2 ?? ''}</div>
              {(billingAddress.city ||
                billingAddress.state ||
                billingAddress.postal_code ||
                billingAddress.province ||
                billingAddress.zip) && (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {`${billingAddress.city ?? ''} ${
                    billingAddress.state ?? billingAddress.province ?? ''
                  } ${billingAddress.postal_code ?? billingAddress.zip ?? ''}`}
                </div>
              )}
              <div>{billingAddress.country ?? ''}</div>
              <div>{billingAddress.phone ?? ''}</div>
            </div>
          ) : (
            <>
              <div className="column is-half">
                <div className="customer-info__label">Billing address</div>
                {isLoading && <Loading isFullWidth />}
                {AddressOption}
                {props.children}
              </div>

              <div className="column">{AddressForm}</div>
            </>
          )} */}
        </div>
      </div>
    </>
  )
}

export default CustomerInfoOrdTracking

const ScopeStyle = css`
  .customer-info {
    &__label {
      font-size: 15px;
      font-weight: bold;
      margin-bottom: 8px;

      &.has-icon {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }

    &__payment {
      display: flex;
      align-items: center;

      &--card {
        width: 56px;
        height: 32px;
        margin-right: 8px;
      }

      &--number {
        font-weight: 600;
      }
    }

    &__upinus {
      display: inline-block;
      margin-bottom: 2px;
      margin-left: 4px;

      img {
        width: auto;
        height: 22px;

        @media screen and (max-width: 960px) and (min-width: 901px) {
          height: 20px;
        }

        @media screen and (max-width: 900px) and (min-width: 821px) {
          height: 16px;
        }

        @media screen and (max-width: 820px) and (min-width: 769px) {
          height: 12px;
        }
      }
    }

    &__subinfo {
      font-size: 12px;
      line-height: 16px;
      margin-top: 8px;
    }

    .payment-merchant {
      font-size: 12px;
      line-height: 24px;
      font-style: italic;
      padding-top: 8px;
    }
  }
`
